'use client';

import { useCurrency } from '@contexts/CurrencyContext';
import { useRouter } from '@contexts/Router/RouterContext';
import { useCookieSettings } from '@lib/hooks/useCookieSettings';
import { useEffect } from 'react';
import { EngineProvider } from './EngineContext';
import { FacetBreadcrumbs } from './FacetBreadcrumbs';
import { HorizontalFilters } from './HorizontalFilters';
import { MobileFilterBar } from './MobileFilterBar';
import { Pager } from './Pager';
import { QuerySummary } from './QuerySummary';
import { GRID_CLASSES, ResultList, SKELETON_COUNT } from './ResultList';
import { SearchEngineError } from './SearchEngineError';
import { Sidebar } from './Sidebar';
import { HorizontalFiltersSkeleton } from './Skeleton/HorizontalFiltersSkeleton';
import { ProductCardSkeleton } from './Skeleton/ProductCardSkeleton';
import { QuerySummarySkeleton } from './Skeleton/QuerySummarySkeleton';
import { SidebarSkeleton } from './Skeleton/SidebarSkeleton';
import { createEngineModule } from './createEngineModule';
import { hasSearchQuery } from './hooks/hasSearchQuery';
import { useEngineState } from './hooks/useEngineState';
import { useUrlManagerSync } from './hooks/useUrlManagerSync';

type EngineModule = ReturnType<typeof createEngineModule>;

type SearchPageLayoutProps = {
  engineModule: EngineModule;
  resultSectionTestId: string;
};

export const SearchPageLayout = ({
  engineModule,
  resultSectionTestId,
}: SearchPageLayoutProps) => {
  const { locale } = useRouter();
  const { currency } = useCurrency();
  const { functionalCategoryEnabled } = useCookieSettings();
  const engineState = useEngineState(engineModule.initEngine, {
    locale,
    currency,
    analyticsEnabled: functionalCategoryEnabled,
  });

  const eng = engineState === 'ready' ? engineModule.engine() : null;
  useUrlManagerSync(eng);

  useEffect(() => {
    if (!eng) return;
    if (functionalCategoryEnabled) {
      eng.enableAnalytics();
    } else {
      eng.disableAnalytics();
    }
  }, [eng, functionalCategoryEnabled]);

  if (engineState === 'error') {
    return <SearchEngineError />;
  }

  if (engineState !== 'ready') {
    return (
      <div className="container min-h-[25rem] pb-10 pt-4 lg:max-w-full lg:px-8 lg:pb-16 lg:pt-12 xl:max-w-full 2xl:max-w-none">
        {hasSearchQuery() && (
          <QuerySummarySkeleton
            variant="title"
            className="header-large mb-1 hidden lg:block"
          />
        )}
        <QuerySummarySkeleton
          variant="count"
          className="body-small mb-4 hidden lg:mb-6 lg:block"
        />
        <div className="flex flex-col gap-0 lg:flex-row lg:gap-5">
          <aside className="hidden w-[304px] min-w-[304px] shrink-0 lg:block">
            <SidebarSkeleton />
          </aside>
          <main className="min-w-0 flex-1">
            {hasSearchQuery() && (
              <QuerySummarySkeleton
                variant="title"
                className="header-large mb-1 lg:hidden"
              />
            )}
            <QuerySummarySkeleton
              variant="count"
              className="body-small mb-4 text-grey-600 lg:hidden"
            />
            <HorizontalFiltersSkeleton className="hidden lg:flex" />
            <div className={`${GRID_CLASSES} mt-4 lg:mt-5`}>
              {Array.from({ length: SKELETON_COUNT }, (_, i) => (
                <ProductCardSkeleton key={`init-skeleton-${i}`} />
              ))}
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <EngineProvider engine={engineModule.engine()}>
      <div className="container min-h-[25rem] pb-10 pt-4 lg:max-w-full lg:px-8 lg:pb-16 lg:pt-12 xl:max-w-full 2xl:max-w-none">
        <QuerySummary
          variant="title"
          className="header-large mb-1 hidden lg:block"
        />
        <QuerySummary
          variant="count"
          className="body-small mb-4 hidden text-grey-600 lg:mb-6 lg:block"
          testId="query-summary-count-desktop"
        />
        <div className="flex flex-col gap-0 lg:flex-row lg:gap-5">
          <aside className="hidden w-[304px] min-w-[304px] shrink-0 lg:block">
            <Sidebar />
          </aside>

          <main className="min-w-0 flex-1">
            <QuerySummary
              variant="title"
              className="header-large mb-1 lg:hidden"
            />
            <QuerySummary
              variant="count"
              className="body-small mb-4 text-grey-600 lg:hidden"
            />
            <MobileFilterBar className="mb-3 lg:hidden" />
            <HorizontalFilters className="hidden lg:flex" />
            <FacetBreadcrumbs className="mt-3 hidden lg:flex" />
            <div className="mt-4 lg:mt-5" data-test={resultSectionTestId}>
              <ResultList />
            </div>
            <div className="mt-4 flex justify-center lg:mt-5">
              <Pager />
            </div>
          </main>
        </div>
      </div>
    </EngineProvider>
  );
};
